import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ESTADO_META, fmtPeriodoCorto } from './components/SectionStatusChip';
import { Gauge, RefreshCw, ChevronRight, Clock, CalendarClock, AlertTriangle, Zap } from 'lucide-react';

const hace = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `hace ${days} d`;
  return d.toLocaleDateString('es-AR');
};

const fmtProxima = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const EstadoCard = ({ s, onClick }) => {
  const meta = ESTADO_META[s.estado] || ESTADO_META.sin_datos;
  const Icon = meta.icon;
  const proxima = fmtProxima((s.automatizaciones || []).map((a) => a.next_run).filter(Boolean).sort()[0]);
  return (
    <Card
      role="button"
      onClick={onClick}
      data-testid={`estado-card-${s.key}`}
      className="group relative overflow-hidden cursor-pointer border-slate-200 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
    >
      <div className={`h-1.5 w-full bg-gradient-to-r ${meta.grad}`} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">{s.nombre}</h3>
            <span className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`} data-testid={`estado-badge-${s.key}`}>
              <Icon size={13} /> {meta.label}
              {s.tiene_error && <AlertTriangle size={12} className="text-rose-600 ml-0.5" />}
            </span>
          </div>
          <div className={`shrink-0 h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${meta.grad} shadow-sm`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>

        <div className="mt-4 space-y-1.5 text-sm">
          {!s.en_vivo && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Último dato</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200" data-testid={`estado-ultimo-${s.key}`}>
                {s.ultimo_periodo ? fmtPeriodoCorto(s.ultimo_periodo) : 'sin datos'}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-slate-400 flex items-center gap-1"><Clock size={12} /> {s.en_vivo ? 'Fuente' : 'Actualizado'}</span>
            <span className="text-slate-600 dark:text-slate-300">{s.en_vivo ? 'En vivo' : hace(s.ultima_actualizacion)}</span>
          </div>
          {proxima && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400 flex items-center gap-1"><CalendarClock size={12} /> Próxima</span>
              <span className="text-slate-600 dark:text-slate-300">{proxima}</span>
            </div>
          )}
          {s.tiene_error && (
            <p className="text-xs text-rose-600 flex items-center gap-1 pt-1"><AlertTriangle size={12} /> La última corrida automática falló</p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end text-xs text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
          Ver sección <ChevronRight size={14} />
        </div>
      </div>
    </Card>
  );
};

const ResumenPill = ({ n, label, dot, testId }) => (
  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-sm" data-testid={testId}>
    <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
    <span className="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{n}</span>
    <span className="text-xs text-slate-500">{label}</span>
  </div>
);

const KpisEstado = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get('/kpis/estado');
      setData(r.data);
    } catch (e) {
      toast.error('Error al cargar el estado de datos');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-estado-loading">Cargando estado de datos…</div>;

  const r = data?.resumen || {};
  const secciones = data?.secciones || [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="kpis-estado">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><Gauge className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Estado de datos</h1>
            <p className="text-sm text-slate-500">Qué tan actualizada está cada sección de KPIs · mes actual {fmtPeriodoCorto(data?.mes_actual)}</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="estado-refresh-btn" className="gap-2">
          <RefreshCw size={15} /> Actualizar
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <ResumenPill n={r.actualizado || 0} label="al día" dot="bg-emerald-500" testId="resumen-actualizado" />
        <ResumenPill n={r.atrasado || 0} label="atrasadas" dot="bg-amber-500" testId="resumen-atrasado" />
        <ResumenPill n={r.problemas || 0} label="con problemas" dot="bg-rose-500" testId="resumen-problemas" />
        {r.en_vivo ? <ResumenPill n={r.en_vivo} label="en vivo" dot="bg-sky-500" testId="resumen-envivo" /> : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {secciones.map((s) => (
          <EstadoCard key={s.key} s={s} onClick={() => navigate(`/m/kpis/${s.key}`)} />
        ))}
      </div>

      <p className="text-xs text-slate-400 mt-6 flex items-center gap-1">
        <Zap size={12} /> 🟢 datos del mes actual o el pasado · 🟡 de 2 meses atrás · 🔴 3+ meses, sin datos o con error · 🔵 en vivo.
      </p>
    </div>
  );
};

export default KpisEstado;
